import { localizedStrings } from '@core/strings'
import React, { useCallback, useEffect, useState } from 'react'
import { BoxProps, Flex, Text } from '@fundamentals'
import { Button, LoadingButton, MenuItem, Tooltip } from '@common/material'
import ArrowBack from '@mui/icons-material/ArrowBack'
import { useRouter } from 'next/router'
import { useIsMounted } from '@core/providers/useIsMounted'
import { useFormContext } from 'react-hook-form'
import { alpha } from '@mui/material'
import { LoadingButtonProps } from '@mui/lab'
import { useFunction } from '@common/hooks'

export type ActionButtonProps = LoadingButtonProps & {
  dataTest?: string
  tooltip?: string
}

export type StagedNavigationLayoutProps<WithSidebar extends boolean = boolean> =
  BoxProps & {
    resetOnMount?: boolean
    withStageSidebar?: WithSidebar
    navigateWithSidebar?: boolean
    stages: Array<
      (WithSidebar extends true ? { label: string } : { label?: string }) & {
        key: string
        component: React.ReactNode
        actionButtons?: Array<
          LoadingButtonProps & { dataTest?: string; tooltip?: string }
        >
        onEntry?: () => void
        onExit?: () => void
        onNextFailed?: () => void
        disabled?: boolean
        tooltip?: string
        dataTest?: string
        header?: React.ReactNode
        isNested?: boolean
        beforeNextStage?: (nextStage: () => void) => void
      }
    >
    actionButtons?: Array<ActionButtonProps>
    ordered?: boolean
    leftPanel?: React.ReactNode
    rightPanel?: React.ReactNode
    onBack?: () => void
    disableNavigation?: boolean
    disableRouting?: boolean
    showStageLabel?: boolean
  }
type ArrayDataType<T extends any[]> = T extends (infer U)[] ? U : never

type StageBuilder = () => {
  addStage: (
    stage: ArrayDataType<StagedNavigationLayoutProps['stages']>,
  ) => ReturnType<StageBuilder>
  build: () => StagedNavigationLayoutProps['stages']
}

const LAYOUT_HEADER_HEIGHT = 72

const StagedNavigationLayout: React.FC<StagedNavigationLayoutProps> & {
  StageBuilder: StageBuilder
} = ({
  title,
  stages,
  actionButtons,
  ordered,
  children,
  leftPanel,
  rightPanel,
  withStageSidebar,
  onBack: onBackProp,
  disableNavigation,
  resetOnMount = true,
  disableRouting,
  showStageLabel = true,
  ...props
}) => {
  const isMounted = useIsMounted()
  const router = useRouter()
  const queryStage = router.query?.stage
  const [localStage, setLocalStage] = useState(stages[0].key)
  const stageKey = !disableRouting ? queryStage : localStage
  const formContext = useFormContext()

  const nextStage = useFunction(() => {
    const index = stages.findIndex((stage) => stage.key === stageKey)
    if (index !== -1 && index !== stages.length - 1) {
      stages[index].onExit?.()
      window.scrollTo({ top: 0 })
      if (!disableRouting) {
        router.query.stage = stages[index + 1].key
        router.push(router, undefined, { shallow: true })
      } else {
        setLocalStage(stages[index + 1].key)
      }
    }
  })

  const onBack = useCallback(() => {
    const index = stages.findIndex((stage) => stage.key === stageKey)
    stages[index].onExit?.()
    if (!disableRouting) {
      router.back()
    } else {
      const prevIndex = index - 1
      prevIndex === -1 ? router.back() : setLocalStage(stages[index - 1]?.key)
    }
  }, [stageKey])

  const {
    component,
    actionButtons: stageActionButtons,
    label,
    onEntry,
    onNextFailed,
    beforeNextStage,
  } = stages.find((stage) => stage.key === stageKey) ?? {}

  useEffect(() => {
    onEntry?.()
  }, [stageKey])

  if (
    !!stages.length &&
    (!queryStage ||
      (!isMounted() && queryStage !== stages[0].key && resetOnMount)) &&
    !disableRouting
  ) {
    if (router.isReady) {
      router.query.stage = stages[0].key
      router.replace(router, undefined, { shallow: true })
    }
    return
  }

  const handleNextStage = () => {
    beforeNextStage ? beforeNextStage(nextStage) : nextStage()
  }

  return (
    <Flex flexDirection='column' flex={1} data-test='staged-navigation-layout'>
      <Flex
        p={3}
        bgcolor='white'
        borderBottom='1px solid lightgrey'
        py={2}
        position='fixed'
        top={0}
        left={0}
        right={0}
        zIndex={100}
        height={LAYOUT_HEADER_HEIGHT}
        alignItems='center'
      >
        <Button
          variant='outlined'
          disabled={disableNavigation}
          onClick={onBackProp || onBack}
          sx={{ mr: 2 }}
          data-test='staged-navigation-layout-back-button'
        >
          <ArrowBack sx={{ fontSize: 27 }} />
        </Button>

        <Flex flex={1} alignItems='center'>
          {label && showStageLabel && <Text mr={1.5}>{label}</Text>}
          {children}
        </Flex>
        {[...(actionButtons ?? []), ...(stageActionButtons ?? [])].map(
          ({ sx, dataTest, ...actionButton }, index) => (
            <Tooltip key={index} title={actionButton?.tooltip}>
              <span>
                <LoadingButton
                  key={index}
                  sx={{ minWidth: 150, ml: 1.5, ...sx }}
                  loadingPosition='end'
                  data-test={dataTest}
                  {...actionButton}
                />
              </span>
            </Tooltip>
          ),
        )}
        {ordered && stageKey !== stages[stages.length - 1].key && (
          <Button
            key='next'
            variant='contained'
            data-test='submit-btn'
            sx={{ width: 150, ml: 1.5 }}
            onClick={
              formContext?.handleSubmit(handleNextStage, onNextFailed) ||
              handleNextStage
            }
          >
            {localizedStrings.next}
          </Button>
        )}
      </Flex>
      <Flex flex={1} overflow='hidden' mt={`${LAYOUT_HEADER_HEIGHT}px`}>
        {withStageSidebar && (
          <Flex
            sx={{
              borderRight: '1px solid',
              borderColor: alpha('#000', 0.12),
              minWidth: '16rem',
              maxWidth: '20rem',
              flexDirection: 'column',
              bgcolor: 'white',
              pt: 2,
            }}
          >
            {stages?.map((stage, index) => (
              <React.Fragment key={stage.key}>
                {stage.header && stage.header}
                <Tooltip title={stage?.tooltip}>
                  <span>
                    <MenuItem
                      disabled={stage.disabled}
                      data-test={stage.dataTest}
                      selected={queryStage === stage.key}
                      sx={{
                        px: '2rem',
                        py: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                      onClick={() => {
                        router.query.stage = stage.key
                        router.push(router, undefined, { shallow: true })
                      }}
                    >
                      <Text ml={stage.isNested && 2}>{stage.label}</Text>
                    </MenuItem>
                  </span>
                </Tooltip>
              </React.Fragment>
            ))}
          </Flex>
        )}

        {leftPanel}
        <Flex flexDirection='column' flex={1} height='100%'>
          <Flex
            flexDirection='column'
            {...props}
            sx={{ flex: 1 }}
            overflow='hidden'
          >
            {component}
          </Flex>
        </Flex>
        {rightPanel}
      </Flex>
    </Flex>
  )
}

StagedNavigationLayout.StageBuilder = function () {
  const stages = [] as StagedNavigationLayoutProps['stages']
  const builder = {
    addStage: (stage: ArrayDataType<StagedNavigationLayoutProps['stages']>) => {
      stages.push(stage)
      return builder
    },
    build: () => stages,
  }
  return builder
}

export { StagedNavigationLayout }
